import { useRef } from 'react';

interface UseBarcodePrintProps {
  asset: {
    id: string;
    barcode?: string;
    assetType?: { name: string };
    brand?: { name: string };
    model?: { name: string };
    serialNumber: string;
  };
}

const getPrintStyles = () => `
  body {
    margin: 0;
    padding: 0;
    font-family: Arial, sans-serif;
  }
  .barcodeContainer {
    width: 8cm;
    height: 4cm;
    border: 1px solid #ccc;
    padding: 0.3cm;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }
  .barcodeSection {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.1cm;
  }
  .logoSection {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    margin-bottom: 0.1cm;
  }
  .companyName {
    font-size: 14px;
    font-weight: bold;
    color: #333;
    text-align: center;
    width: 100%;
  }
  .assetInfo {
    text-align: center;
    margin-bottom: 0.05cm;
  }
  .assetModel {
    font-size: 9px;
    color: #888;
  }
  .assetType {
    font-size: 8px;
    color: #888;
    margin-bottom: 0.02cm;
  }
  .serialNumber {
    font-size: 8px;
    color: #666;
    margin-bottom: 0.02cm;
  }
  .barcodeValue {
    font-size: 10px;
    font-weight: bold;
    margin-top: 0.05cm;
    text-align: center;
  }
  .barcode-svg {
    margin: 0.05cm 0;
    max-height: 1.8cm;
    width: auto;
  }
  .barcode-svg svg {
    max-height: 1.8cm;
    width: auto;
  }
  @media print {
    body { 
      margin: 0; 
      padding: 0;
    }
    .barcodeContainer { 
      border: none; 
      page-break-inside: avoid;
    }
    /* Ocultar fecha y hora de impresión */
    @page {
      margin: 0;
      size: auto;
    }
    /* Ocultar headers y footers del navegador */
    @page :first {
      margin-top: 0;
    }
    @page :left {
      margin-left: 0;
    }
    @page :right {
      margin-right: 0;
    }
    /* Ocultar información del navegador */
    html {
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
  }
`;

const generatePrintHTML = (asset: UseBarcodePrintProps['asset'], barcodeSvgString: string) => `
  <!DOCTYPE html>
  <html>
    <head>
      <title>Código de Barras - ${asset.serialNumber}</title>
      <style>${getPrintStyles()}</style>
    </head>
    <body>
      <div class="barcodeContainer">
        <div class="barcodeSection">
          <div class="logoSection">
            <div class="companyName">SOYDELIVERY</div>
          </div>
          <div class="assetInfo">
            <div class="assetType">${asset.assetType?.name || 'Tipo'}</div>
            <div class="assetModel">${asset.brand?.name || 'Marca'} - ${asset.model?.name || 'Modelo'}</div>
            <div class="serialNumber">S/N: ${asset.serialNumber}</div>
          </div>
          <div class="barcode-svg">
            ${barcodeSvgString}
          </div>
          <div class="barcodeValue">${asset.barcode}</div>
        </div>
      </div>
    </body>
  </html>
`;

export const useBarcodePrint = ({ asset }: UseBarcodePrintProps) => {
  const printRef = useRef<HTMLDivElement>(null);

  const handlePrint = () => {
    try {
      if (!asset.barcode) {
        console.warn('No hay código de barras para imprimir');
        return;
      }

      if (!printRef.current) {
        console.warn('Referencia del elemento de impresión no encontrada');
        return;
      }

      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        console.warn('No se pudo abrir la ventana de impresión');
        return;
      }

      const barcodeSvg = printRef.current.querySelector('svg');
      const barcodeSvgString = barcodeSvg ? barcodeSvg.outerHTML : '';

      const printHTML = generatePrintHTML(asset, barcodeSvgString);

      printWindow.document.write(printHTML);
      printWindow.document.close();
      printWindow.focus();
      
      setTimeout(() => {
        printWindow.print();
      }, 1000);
    } catch (error) {
      console.error('Error en handlePrint:', error);
    }
  };

  return {
    printRef,
    handlePrint,
    hasBarcode: !!asset.barcode,
  };
};
